import { authorizedFetch, ErrorResponse } from './api-client';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// --- Agendamentos ---
export type AppointmentStatus =
  | 'booked'
  | 'fit_in'
  | 'canceled'
  | 'unbooked'
  | 'no_show'
  | 'rescheduled'
  | 'arrived'
  | 'in_progress'
  | 'attended'
  | 'not_attended';

export interface AppointmentData {
  id: string;
  patient_name: string;
  patient_cpf: string;
  patient_cellphone: string;
  patient_email: string;
  patient_birth_date?: string;
  patient_card_number?: string;
  patient_last_registry_review_at?: string | null;
  patient_insurance?: string;
  patient_dental_insurance_id?: string;
  patient_dental_insurance_name?: string;
  confirmed?: boolean;
  appointment_card_number?: string;
  appointment_dental_insurance_id?: string;
  appointment_dental_insurance_name?: string;
  card_number?: string;
  appointment_number: string;
  clinic_name: string;
  consulting_room_name: string;
  dental_specialty_name: string;
  dentist_name: string;
  dental_insurance_name?: string;
  slot_date: string;
  slot_time: string;
  appointment_status: AppointmentStatus;
  status: number;
  old_appointment_id: string | null;
  schedule_slot_id: string;
  clinic_id: string;
  consulting_room_id: string;
  dental_specialty_id: string;
  dentist_id: string;
  patient_id: string;
  dental_insurance_id?: string;
  observation?: string;
  observations?: string;
  import_id?: string;
  duration_minutes?: number;   // ← NOVO: 30 ou 60
}

export async function fetchAppointments(
  includeInactive = false,
  complete = false,
  clinicId?: string,
  patientId?: string,
  consultingRoomId?: string,
  dentalSpecialtyId?: string,
  dentistId?: string,
  startDate?: string,
  endDate?: string,
  appointmentStatus?: string,
  appointmentNumber?: string,
  likePatientName?: string,
  likePatientCpf?: string,
  likePatientCellphone?: string, // Adicionado como 14º argumento
  page = 1,
  pageSize = 20
): Promise<AppointmentData[]> {
  const params = new URLSearchParams({
    include_inactive: includeInactive.toString(),
    complete: complete.toString(),
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (clinicId) params.append('clinic_id', clinicId);
  if (patientId) params.append('patient_id', patientId);
  if (consultingRoomId) params.append('consulting_room_id', consultingRoomId);
  if (dentalSpecialtyId) params.append('dental_specialty_id', dentalSpecialtyId);
  if (dentistId) params.append('dentist_id', dentistId);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (appointmentStatus) params.append('appointment_status', appointmentStatus);
  if (appointmentNumber) params.append('appointment_number', appointmentNumber);
  if (likePatientName) params.append('like_patient_name', likePatientName);
  if (likePatientCpf) params.append('like_patient_cpf', likePatientCpf);
  if (likePatientCellphone) params.append('like_patient_cellphone', likePatientCellphone); // Novo append

  const url = `${API_BASE_URL}/api/v1/appointment/?${params.toString()}`;
  const response = await authorizedFetch(url);

  if (!response.ok) {
    throw new Error('Erro ao buscar agendamentos');
  }

  const data = await response.json();
  return data.items ?? data;
}

export async function fetchAppointmentById(appointmentId: string): Promise<AppointmentData> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/appointment/${appointmentId}`);

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao buscar agendamento');
  }

  return response.json();
}

// Fit-in Appointment
export async function createFitInAppointment(data: {
  clinic_id: string;
  consulting_room_id: string;
  appointment_date: string;
  appointment_time: string;
  dentist_id: string;
  dental_specialty_id: string;
  patient_id: string;
  dental_insurance_id: string;
  card_number: string;
  import_key?: string;
}): Promise<AppointmentData> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/appointment/fit-in`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao criar encaixe');
  }

  return response.json();
}

// Update Appointment Arrived
export async function updateAppointmentArrived(
  appointmentId: string,
  data: {
    patient_id: string;
    dentist_id: string;
    dental_insurance_id: string;
    card_number: string;
    observations: string;
  }
): Promise<void> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/appointment/${appointmentId}/arrived`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao atualizar status para chegou');
  }
}

export interface ChangeStatusAppointmentData {
  patient_id: string;
  new_status: string;
  dentist_id: string;
  dental_insurance_id: string;
  card_number: string;
  observations: string;
}

export async function changeAppointmentStatus(
  appointmentId: string,
  data: ChangeStatusAppointmentData
): Promise<void> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/appointment/${appointmentId}/change-status`,
    {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    }
  );

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || "Erro ao alterar status do agendamento");
  }
}


